// Quick view, a new scene: on an episode with no scenes yet, first "Pick a style" (the drawing styles as sample tiles,
// the last one used picked; it is remembered). From then on the episode's style is its first scene's, and every new
// scene, character and place made here paints in it. When the episode already has scenes, first a choice: cut back to an earlier scene (a copy of
// it goes in at the end at once, see Earlier.tsx) or draw a new one. Drawing goes: who's in it (the library's characters, or a new one from NewCharacter.tsx), where it is (one
// of the library's places with a picture, a new one from NewCharacter.tsx, or "Nowhere in particular", the default),
// then a blank panel to write what's happening in. "Draw it" fills that same panel with the drawing, with "Looks good" or "Try again"
// (back to the words, to change them and draw again). The picture goes at the end of the episode with the characters'
// faces found on it (any not found are spread across it; "Faces in the wrong place?" opens the face placer). Drawing
// keeps going if the page is left (ui/jobs.ts) and lands in the episode wherever it is. A new episode's first scene
// names it after the words. The characters and the place go to the painting as references (the place as a "background"
// one, the way the full image maker sends it); no props are offered.
import { useEffect, useRef, useState } from "react";
import { hasPicture } from "../../library/build";
import { imageSrc } from "../../library/images";
import { markUsed, startDraft } from "../../library/drafts";
import { getLibrary, useLibrary } from "../../library/store";
import { uid } from "../../model/ids";
import { findAsset } from "../../model/library";
import {
  addPicture, copyScene, duration, markById, marksOf, setMarker, setPictureFaces, spreadFaces,
  type PaintSource, type PictureVersionFaces, type PlacedFace,
} from "../../model/timeline";
import { timelineFrameAt } from "../../engine/timelineFrame";
import { FrameSvg } from "../../render/FrameSvg";
import { api } from "../api";
import { Avatar, Body, DoneFooter, Footer, Icon, Layer, Page, TopBar, useIdleClock } from "../common";
import { lastStyleId, rememberStyle, useStyles } from "../images/styles";
import { StyleSample } from "../images/ImageMaker";
import "../images/imageMaker.css";
import { notify, picJob, runJob, useJob } from "../jobs";
import { applyToEpisode } from "../timeline/episodeStore";
import { useTimeline } from "../timeline/TimelineLoader";
import { FacePlacing } from "../timeline/pages/FacePlacing";
import { keptVersions } from "../timeline/pages/PictureMaker";
import { refCharacterIds, useCharacters } from "../timeline/pages/shared";
import { account } from "../account";
import { SignIn } from "../landing/SignIn";
import { fixPaintChoice } from "../common/ModelSelect";
import { QUICK_PAINT } from "./paint";
import { EarlierScenes } from "./Earlier";
import { NewCharacter } from "./NewCharacter";
import { Drawing, Panel, Problem, paintProblem } from "./Panel";

type Step = "style" | "start" | "earlier" | "who" | "where" | "what" | "made";

// The name a new episode gets on the server, and the name its first scene's words give it instead: the first few words.
const UNTITLED = "Untitled episode";
function titleFrom(text: string): string {
  const words = text.split(/[.!?\n]/)[0]!.trim().split(/\s+/).slice(0, 6).join(" ");
  return words ? words[0]!.toUpperCase() + words.slice(1) : UNTITLED;
}

export function NewScene({ onClose }: { onClose: () => void }) {
  const { ep, update } = useTimeline();
  const lib = useLibrary();
  const chars = useCharacters().filter(hasPicture);
  const places = lib.assets.filter(a => a.kind === "background" && hasPicture(a));
  const clock = useIdleClock();
  const [step, setStep] = useState<Step>(() => (ep.pictures.length ? "start" : "style")); // (no scenes to cut back to: pick the style first)
  const had = useRef(ep.pictures.length > 0).current; // the episode had scenes when this page opened
  const styles = useStyles();
  const [styleId, setStyleId] = useState(lastStyleId); // the style picked for a new episode
  const [picked, setPicked] = useState<string[]>([]);
  const [place, setPlace] = useState<string | null>(null); // where it is; null: nowhere in particular
  const [making, setMaking] = useState<{ id: string; kind: "character" | "background" } | null>(null); // a new character's or place's draft being made
  const [words, setWords] = useState("");
  const [facing, setFacing] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [signing, setSigning] = useState(false); // the sign-in sheet, asked for at the first "Draw it"
  const markerId = useRef(uid("pk")).current;
  const since = useJob(picJob(markerId));
  const painting = since !== null;
  const here = useRef(true);
  useEffect(() => { fixPaintChoice(QUICK_PAINT); return () => fixPaintChoice(null); }, []); // (also covers NewCharacter, which opens inside)
  useEffect(() => { here.current = true; return () => { here.current = false; }; }, []);
  const mark = markById(ep, "picture", markerId);
  // The style everything made here is painted in: the episode's own once it has a scene, else the one picked.
  const epText = marksOf(ep, "picture")[0]?.style;
  const pickedStyle = styles.find(s => s.id === styleId) ?? styles[0]!;
  const style: { text: string; id?: string } = epText ? { text: epText, id: styles.find(s => s.text === epText)?.id } : pickedStyle;

  // A copy of an earlier scene goes in at the end of the episode (one undo step), and this page closes.
  function reuse(pictureId: string) {
    update(e => copyScene(e, pictureId).ep);
    onClose();
  }

  const toggle = (id: string) => setPicked(p => (p.includes(id) ? p.filter(x => x !== id) : [...p, id]));

  // Draws the picture from the picked characters and the words: the first time it is added at the end of the episode,
  // later ones replace it on the same marker and are kept as its versions.
  function paint() {
    const refs: PaintSource[] = [...picked, ...(place ? [place] : [])].flatMap(id => { const a = findAsset(lib, id); return a && hasPicture(a) ? [{ src: a.picture.src, name: a.name, kind: a.kind as PaintSource["kind"] }] : []; });
    const text = words.trim();
    markUsed(refs);
    setProblem(null);
    const work = (async () => {
      const { faces: found = [], ...picture } = await api.paintShot(refs, text, style.text, true);
      const now = getLibrary();
      const charOf = (src: string) => now.assets.find(a => a.kind === "character" && a.picture.src === src)?.id;
      const faces = found.flatMap(f => { const char = charOf(f.ref); return char ? [{ char, face: f.face }] : []; });
      const placed: PlacedFace[] = [...faces, ...spreadFaces([...new Set(refCharacterIds(now, refs))], picture).filter(s => !faces.some(f => f.char === s.char))];
      const v: PictureVersionFaces = { id: uid("v"), picture, how: "drawn", text, at: Date.now(), ...(faces.length ? { faces } : {}) };
      const fields = { picture, prompt: text, refs, faces: placed, style: style.text };
      await applyToEpisode(ep.id, e => {
        const m = markById(e, "picture", markerId);
        if (m) return setMarker(e, "picture", markerId, { ...fields, versions: [...keptVersions(m), v] });
        const named = !e.pictures.length && e.title === UNTITLED ? { ...e, title: titleFrom(text) } : e;
        return addPicture(named, duration(named), { ...fields, versions: [v] }, markerId).ep;
      }, { undoable: true });
    })();
    runJob(picJob(markerId), work)
      .then(() => { if (here.current) setStep("made"); })
      .catch(e => { const m = paintProblem((e as Error).message); if (here.current) setProblem(m); else notify(`Your new scene: ${m}`); });
  }

  // Back: a step back, or out while it is drawn (it carries on and lands in the episode).
  const back = () => (painting ? onClose() : step === "made" ? setStep("what") : step === "what" ? setStep("where") : step === "where" ? setStep("who") : step === "who" ? setStep(had ? "start" : "style") : step === "earlier" ? setStep("start") : onClose());

  if (making) {
    return <NewCharacter assetId={making.id} kind={making.kind} style={style} onBack={() => setMaking(null)}
      onUse={id => { setMaking(null); if (making.kind === "background") setPlace(id); else setPicked(p => (p.includes(id) ? p : [...p, id])); }} />;
  }

  if (facing && mark) {
    return (
      <Layer>
        <Page className="sm">
          <TopBar onBack={() => setFacing(false)} title="Move the faces" />
          <Body>
            <FacePlacing ep={ep} t={mark.t + 0.01} picture={mark.picture} faces={mark.faces} refs={mark.refs} fixedCast
              onFaces={faces => update(e => setPictureFaces(e, markerId, faces), { merge: `faces-${markerId}` })} />
          </Body>
          <DoneFooter onDone={() => setFacing(false)} />
        </Page>
      </Layer>
    );
  }

  const showMade = step === "made" && !painting && mark;
  return (
    <Layer>
      <Page className="sm">
        <TopBar onBack={back} title={step === "style" || step === "start" || step === "earlier" || step === "who" || step === "where" ? undefined : "New scene"} />
        <Body className="sm-stage" page={step}>
          {step === "style" && (
            <>
              <h1 className="sm-ask">Pick a style</h1>
              <div className="pp-style-grid sm-styles" role="radiogroup" aria-label="Styles">
                {styles.map(s => (
                  <div key={s.id} className={`pp-style-tile${s.id === pickedStyle.id ? " on" : ""}`}>
                    <button className="pp-style-pick" role="radio" aria-checked={s.id === pickedStyle.id} title={s.text} onClick={() => { setStyleId(s.id); rememberStyle(s.id); }}>
                      <StyleSample style={s} big />
                      <b>{s.name}</b>
                      {s.id === pickedStyle.id && <span className="tick"><Icon name="check" size={18} /></span>}
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
          {step === "start" && (
            <>
              <h1 className="sm-ask">New scene</h1>
              <div className="sm-start">
                <button className="btn big wide" onClick={() => setStep("earlier")}><Icon name="image" /> Use an earlier scene</button>
                <button className="btn big yellow wide" onClick={() => setStep("who")}><Icon name="plus" /> Draw a new one</button>
              </div>
            </>
          )}
          {step === "earlier" && (
            <>
              <h1 className="sm-ask">Which scene?</h1>
              <EarlierScenes ep={ep} onPick={reuse} idPrefix="sm-pick" className="grid" />
            </>
          )}
          {step === "who" && (
            <>
              <h1 className="sm-ask">Who's in it?</h1>
              <div className="sm-cast" role="group" aria-label="Characters">
                <button className="sm-person new" onClick={() => setMaking({ id: startDraft("character"), kind: "character" })}>
                  <span className="sm-face"><Icon name="plus" size={30} /></span>
                  New
                </button>
                {chars.map(a => {
                  const on = picked.includes(a.id);
                  return (
                    <button key={a.id} className={`sm-person${on ? " on" : ""}`} aria-pressed={on} onClick={() => toggle(a.id)}>
                      <span className="sm-face"><Avatar assetId={a.id} size={84} emotion={on ? "happy" : "neutral"} />{on && <span className="sm-tick"><Icon name="check" size={14} /></span>}</span>
                      {a.name}
                    </button>
                  );
                })}
              </div>
            </>
          )}
          {step === "where" && (
            <>
              <h1 className="sm-ask">Where is it?</h1>
              <div className="sm-cast" role="radiogroup" aria-label="Places">
                <button className="sm-person new" onClick={() => setMaking({ id: startDraft("background"), kind: "background" })}>
                  <span className="sm-face"><Icon name="plus" size={30} /></span>
                  New
                </button>
                <button role="radio" aria-checked={place === null} className={`sm-person${place === null ? " on" : ""}`} onClick={() => setPlace(null)}>
                  <span className="sm-face nowhere"><Icon name="image" size={30} />{place === null && <span className="sm-tick"><Icon name="check" size={14} /></span>}</span>
                  Nowhere in particular
                </button>
                {places.map(a => {
                  const on = place === a.id;
                  return (
                    <button key={a.id} role="radio" aria-checked={on} className={`sm-person${on ? " on" : ""}`} onClick={() => setPlace(a.id)}>
                      <span className="sm-face place"><img src={imageSrc(a.picture.src)} alt="" draggable={false} />{on && <span className="sm-tick"><Icon name="check" size={14} /></span>}</span>
                      {a.name}
                    </button>
                  );
                })}
              </div>
            </>
          )}
          {(step === "what" || step === "made") && (
            <>
              {showMade ? <button className="link-btn sm-fix" onClick={() => setFacing(true)}>Faces in the wrong place?</button> : <h1 className="sm-ask">What's happening?</h1>}
              <Panel corners={!painting && !showMade}>
                {painting ? <Drawing message="Drawing the scene…" since={since} />
                  : showMade ? <FrameSvg id="sm-made" boil={false} frame={timelineFrameAt(ep, mark.t + 0.01, clock)} />
                  : (
                    <>
                      <span className="sm-who-stack">{picked.map(id => <Avatar key={id} assetId={id} size={34} />)}</span>
                      <textarea className="sm-write" value={words} autoFocus aria-label="What's happening"
                        placeholder="What are they up to? e.g. Arguing over the last slice of toast"
                        onChange={e => { setWords(e.target.value); setProblem(null); }} />
                      {problem && <Problem>{problem}</Problem>}
                    </>
                  )}
              </Panel>
            </>
          )}
        </Body>
        {step === "style" && (
          <Footer><button className="btn big yellow wide" onClick={() => setStep("who")}>Next <Icon name="chevron" /></button></Footer>
        )}
        {step === "who" && (
          <Footer><button className="btn big yellow wide" disabled={!picked.length} onClick={() => setStep("where")}>Next <Icon name="chevron" /></button></Footer>
        )}
        {step === "where" && (
          <Footer><button className="btn big yellow wide" onClick={() => setStep("what")}>Next <Icon name="chevron" /></button></Footer>
        )}
        {(step === "what" || step === "made") && !painting && (showMade ? (
          <Footer>
            <button className="btn big" onClick={() => setStep("what")}><Icon name="edit" /> Try again</button>
            <button className="btn big yellow" onClick={onClose}><Icon name="check" /> Looks good</button>
          </Footer>
        ) : (
          <Footer><button className="btn big yellow wide" disabled={!words.trim()} onClick={() => (account() ? paint() : setSigning(true))}><Icon name="sparkle" /> Draw it</button></Footer>
        ))}
        {signing && <SignIn onDone={() => { setSigning(false); paint(); }} onClose={() => setSigning(false)} />}
      </Page>
    </Layer>
  );
}
